import { api, fetchAsset } from '../utils/api';
import {
  getManifest,
  setManifest,
  getCachedAsset,
  cacheAsset,
  getCachedAssetIds,
  deleteCachedAsset,
} from '../utils/storage';
import type { DeviceManifest, ManifestAsset } from '../types';
import { PLAYER_CONFIG } from '../config';
import { eventReporter } from './event-reporter';

export type ManifestUpdateCallback = (manifest: DeviceManifest) => void;

export interface DownloadProgress {
  contentSyncPercent: number;
  pendingDownloads: number;
  failedDownloads: number;
}

class ManifestLoader {
  private manifest: DeviceManifest | null = null;
  private refreshInterval: ReturnType<typeof setInterval> | null = null;
  private onUpdate: ManifestUpdateCallback | null = null;
  private isLoading = false;
  private _downloadProgress: DownloadProgress = { contentSyncPercent: 100, pendingDownloads: 0, failedDownloads: 0 };
  private objectUrlCache = new Map<string, string>(); // Cache object URLs to prevent memory leaks

  /**
   * Load cached manifest from IndexedDB (fast, no network).
   * Also runs cleanup to remove any lingering unused assets from previous manifests.
   * Returns the cached manifest if available, null otherwise.
   */
  async loadCached(): Promise<DeviceManifest | null> {
    const cached = await getManifest();
    if (cached) {
      this.manifest = cached;

      // Run cleanup on boot to remove any lingering unused assets
      const manifestAssetIds = new Set(cached.assets.map(a => a.id));
      const cachedIds = await getCachedAssetIds();
      const toRemove = cachedIds.filter(id => !manifestAssetIds.has(id));

      if (toRemove.length > 0) {
        console.log(`[ManifestLoader] Boot cleanup: removing ${toRemove.length} unused assets...`);
        let cleanupCount = 0;
        let cleanupFailed = 0;
        for (const id of toRemove) {
          try {
            // Revoke object URL if cached to free memory
            const cachedUrl = this.objectUrlCache.get(id);
            if (cachedUrl) {
              URL.revokeObjectURL(cachedUrl);
              this.objectUrlCache.delete(id);
            }
            await deleteCachedAsset(id);
            cleanupCount++;
            console.log(`[ManifestLoader] Boot cleanup: deleted ${id}`);
          } catch (error) {
            cleanupFailed++;
            console.warn(`[ManifestLoader] Boot cleanup: failed to delete ${id}:`, error);
          }
        }
        console.log(`[ManifestLoader] Boot cleanup complete: ${cleanupCount}/${toRemove.length} deleted`);

        if (cleanupCount > 0 || cleanupFailed > 0) {
          eventReporter.reportStorageCleanup(cleanupCount, cleanupFailed);
        }
      }
    }
    return this.manifest;
  }

  async initialize(): Promise<DeviceManifest | null> {
    // Try to load cached manifest first
    await this.loadCached();

    // Try to fetch fresh manifest
    try {
      await this.refresh();
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);

      // Re-throw auth errors so they can be handled at the top level
      if (errorMsg.includes('401') || errorMsg.includes('Unauthorized')) {
        console.error('Authentication failed, re-throwing:', error);
        throw error;
      }

      console.warn('Failed to fetch manifest, using cached:', error);
    }

    return this.manifest;
  }

  async refresh(): Promise<DeviceManifest | null> {
    // Prevent concurrent refresh calls - if one is in progress, return current manifest
    if (this.isLoading) {
      console.log('[ManifestLoader] Refresh already in progress, skipping...');
      return this.manifest;
    }

    this.isLoading = true;

    try {
      const manifest = await api.getManifest();

      // Check if manifest has changed (version OR active playlist)
      const hasChanged =
        !this.manifest ||
        this.manifest.version !== manifest.version ||
        this.manifest.schedule?.activePlaylistId !== manifest.schedule?.activePlaylistId;

      if (hasChanged) {
        // Pre-cache assets for the manifest BEFORE applying it
        const allDownloadsSucceeded = await this.precacheAssets(manifest);

        if (allDownloadsSucceeded) {
          // Only apply new manifest if all downloads succeeded
          this.manifest = manifest;
          await setManifest(manifest);

          // Acknowledge manifest with cached asset hashes
          await this.acknowledgeManifest(manifest);

          // Notify listener
          if (this.onUpdate) {
            this.onUpdate(manifest);
          }
        } else {
          // Keep old manifest active - don't update this.manifest
          console.warn(`[ManifestLoader] Downloads failed, keeping old manifest as fallback`);
        }
      }

      return this.manifest;
    } finally {
      this.isLoading = false;
    }
  }

  startAutoRefresh(callback?: ManifestUpdateCallback): void {
    if (callback) {
      this.onUpdate = callback;
    }

    // Clear existing interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    // Use default interval (new manifest format doesn't have settings)
    const intervalMs = PLAYER_CONFIG.MANIFEST_REFRESH_INTERVAL_MS;

    this.refreshInterval = setInterval(async () => {
      try {
        await this.refresh();
      } catch (error) {
        console.error('Auto-refresh failed:', error);
      }
    }, intervalMs);
  }

  stopAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  getManifest(): DeviceManifest | null {
    return this.manifest;
  }

  getDownloadProgress(): DownloadProgress {
    return this._downloadProgress;
  }

  // Get asset by ID from manifest
  getAsset(assetId: string): ManifestAsset | undefined {
    return this.manifest?.assets.find((a) => a.id === assetId);
  }

  private async acknowledgeManifest(manifest: DeviceManifest): Promise<void> {
    try {
      const cachedIds = new Set(await getCachedAssetIds());

      // Collect SHA256 hashes only for assets that are actually cached and have a hash
      const cachedAssets: string[] = [];
      for (const asset of manifest.assets) {
        if (cachedIds.has(asset.id) && asset.sha256) {
          cachedAssets.push(asset.sha256);
        }
      }

      await api.acknowledgeManifest({
        manifestVersion: manifest.version,
        cachedAssets,
      });

      console.log(`Manifest v${manifest.version} acknowledged with ${cachedAssets.length} cached assets`);
    } catch (error) {
      // Non-critical — don't break manifest loading
      console.warn('Failed to acknowledge manifest:', error);
    }
  }

  private async precacheAssets(manifest: DeviceManifest): Promise<boolean> {
    // Get all assets from manifest (they're already deduplicated in assets array)
    const manifestAssets = new Map<string, ManifestAsset>();
    for (const asset of manifest.assets) {
      manifestAssets.set(asset.id, asset);
    }

    // Get already cached assets
    const cachedIds = new Set(await getCachedAssetIds());

    // Step 1: Download new assets first
    const uncached = [...manifestAssets.entries()].filter(
      ([id]) => !cachedIds.has(id)
    );

    const totalAssets = manifestAssets.size;
    let cachedCount = totalAssets - uncached.length;
    let failedCount = 0;

    // Update initial progress
    this._downloadProgress = {
      contentSyncPercent: totalAssets > 0 ? Math.round((cachedCount / totalAssets) * 100) : 100,
      pendingDownloads: uncached.length,
      failedDownloads: 0,
    };

    console.log(`[ManifestLoader] Caching ${uncached.length} new assets...`);

    // Download with concurrency limit of 3
    const MAX_CONCURRENT = 3;
    let index = 0;

    const updateProgress = () => {
      this._downloadProgress = {
        contentSyncPercent: totalAssets > 0 ? Math.round((cachedCount / totalAssets) * 100) : 100,
        pendingDownloads: totalAssets - cachedCount - failedCount,
        failedDownloads: failedCount,
      };
    };

    const worker = async () => {
      while (index < uncached.length) {
        const i = index++;
        const [id, asset] = uncached[i];
        try {
          await this.cacheAssetItem(id, asset);
          cachedCount++;
        } catch (error) {
          console.warn(`[ManifestLoader] Failed to cache asset ${id}:`, error);
          failedCount++;
        }
        updateProgress();
      }
    };

    // Launch workers up to concurrency limit
    const workers = Array.from(
      { length: Math.min(MAX_CONCURRENT, uncached.length) },
      () => worker()
    );
    await Promise.all(workers);

    console.log(`[ManifestLoader] Asset sync complete: ${cachedCount}/${totalAssets} cached, ${failedCount} failed`);

    // Step 2: Cleanup unused assets only after all downloads succeed
    // This ensures old content remains available as fallback if downloads fail
    if (failedCount === 0) {
      const toRemove = [...cachedIds].filter((id) => !manifestAssets.has(id));
      if (toRemove.length > 0) {
        console.log(`[ManifestLoader] Cleaning up ${toRemove.length} unused assets...`);
        let cleanupCount = 0;
        let cleanupFailed = 0;
        for (const id of toRemove) {
          try {
            // Revoke object URL if cached to free memory
            const cachedUrl = this.objectUrlCache.get(id);
            if (cachedUrl) {
              URL.revokeObjectURL(cachedUrl);
              this.objectUrlCache.delete(id);
            }
            await deleteCachedAsset(id);
            cleanupCount++;
            console.log(`[ManifestLoader] Deleted unused asset: ${id}`);
          } catch (error) {
            // Continue cleanup even if individual deletions fail
            cleanupFailed++;
            console.warn(`[ManifestLoader] Failed to delete asset ${id}:`, error);
          }
        }
        console.log(`[ManifestLoader] Cleanup complete: ${cleanupCount}/${toRemove.length} assets deleted`);

        // Report cleanup to server for visibility in device history
        eventReporter.reportStorageCleanup(cleanupCount, cleanupFailed);
      }
      return true; // All downloads succeeded
    } else {
      console.log(`[ManifestLoader] Skipping cleanup - ${failedCount} downloads failed, keeping old assets as fallback`);
      return false; // Some downloads failed
    }
  }

  private async cacheAssetItem(id: string, asset: ManifestAsset): Promise<void> {
    const blob = await fetchAsset(asset.url);

    // SHA-256 integrity verification (if checksum available)
    if (asset.sha256) {
      const arrayBuffer = await blob.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      if (hashHex !== asset.sha256) {
        console.warn(`[ManifestLoader] SHA-256 mismatch for asset ${id}: expected ${asset.sha256}, got ${hashHex}. Retrying...`);

        // Retry once
        const retryBlob = await fetchAsset(asset.url);
        const retryBuffer = await retryBlob.arrayBuffer();
        const retryHashBuffer = await crypto.subtle.digest('SHA-256', retryBuffer);
        const retryHashHex = Array.from(new Uint8Array(retryHashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');

        if (retryHashHex !== asset.sha256) {
          throw new Error(`SHA-256 verification failed for asset ${id} after retry`);
        }

        console.log(`[ManifestLoader] Asset ${id} verified on retry`);
        const retryBlobFinal = new Blob([retryBuffer], { type: blob.type });
        const mimeType = asset.type === 'video' ? 'video/mp4' : 'image/jpeg';
        // Skip eviction during bulk downloads to preserve fallback assets
        await cacheAsset(id, asset.url, retryBlobFinal, mimeType, true);
        return;
      }

      console.log(`[ManifestLoader] Asset ${id} SHA-256 verified`);
      // Reconstruct blob from already-consumed arrayBuffer
      const verifiedBlob = new Blob([arrayBuffer], { type: blob.type });
      const mimeType = asset.type === 'video' ? 'video/mp4' : 'image/jpeg';
      // Skip eviction during bulk downloads to preserve fallback assets
      await cacheAsset(id, asset.url, verifiedBlob, mimeType, true);
      return;
    }

    const mimeType = asset.type === 'video' ? 'video/mp4' : 'image/jpeg';
    // Skip eviction during bulk downloads to preserve fallback assets
    await cacheAsset(id, asset.url, blob, mimeType, true);
  }

  /**
   * Check if all assets in the current manifest are cached locally.
   * Used for smart loading — skip loading screen when cached content available.
   */
  async hasAllAssetsCached(): Promise<boolean> {
    if (!this.manifest || !this.manifest.assets || this.manifest.assets.length === 0) {
      return false;
    }

    const cachedIds = new Set(await getCachedAssetIds());
    return this.manifest.assets.every(asset => cachedIds.has(asset.id));
  }

  /**
   * Synchronous accessor for the in-memory blob: URL of an asset.
   * Returns null when the object URL hasn't been materialized yet (call
   * getAssetUrl() to lazy-create it from IndexedDB). Used by renderer's
   * buildAssetElement which must avoid `await` between event-loop ticks
   * so the new <video> element gets created and DOM-attached in the
   * same task — otherwise Chromium defers media loading for the
   * still-detached element.
   */
  getCachedObjectUrl(assetId: string): string | null {
    return this.objectUrlCache.get(assetId) || null;
  }

  async getAssetUrl(assetId: string): Promise<string> {
    // Check object URL cache first to prevent memory leaks
    const cachedUrl = this.objectUrlCache.get(assetId);
    if (cachedUrl) {
      return cachedUrl;
    }

    // Try to get from IndexedDB cache
    const cached = await getCachedAsset(assetId);

    if (cached) {
      const objectUrl = URL.createObjectURL(cached);
      this.objectUrlCache.set(assetId, objectUrl);
      return objectUrl;
    }

    // Fallback to asset URL from manifest
    const asset = this.getAsset(assetId);
    return asset?.url || '';
  }

  /**
   * Revoke all cached object URLs to free memory.
   * Call this when manifest changes to clean up old asset URLs.
   */
  revokeObjectUrls(): void {
    for (const url of this.objectUrlCache.values()) {
      URL.revokeObjectURL(url);
    }
    this.objectUrlCache.clear();
  }

  /**
   * Clean up all resources. Call this when the loader is no longer needed.
   */
  destroy(): void {
    this.stopAutoRefresh();
    this.revokeObjectUrls();
    this.manifest = null;
    this.onUpdate = null;
  }
}

export const manifestLoader = new ManifestLoader();
